import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, EventEmitter, Injectable, signal } from '@angular/core';
import { BDTechIncidentStatusUpdate, DashboardStats, DelayAdded, EtaConfirm, Incident, IncidentCloseRequest, IncidentDelayRequest, IncidentEtaUpdate, IncidentLogRequest, IncidentSearchRequest, ServiceProviderStatusUpdate, UploadingFile } from '../models/incident';
import { environment } from '../../environments/environment';
import { Document } from '../models/incident';
import { Client } from '../models/client';
import { catchError, forkJoin, map, Observable } from 'rxjs';
import { AuthService } from '../auth.service';
import { NotificationService } from './notification.service';


@Injectable({
  providedIn: 'root'
})
export class IncidentService {
  
  apiUrl = 'v1/incident/';

  public openIncidents = signal<Incident[]>([]);
  public closedIncidents = signal<Incident[]>([]);
  public operatorIncidents: Incident[] = [];

  public DriverIncident: Incident | undefined = undefined;
  public $incidentUpdate: EventEmitter<Incident> = new EventEmitter<Incident>();
  public $etaUpdate: EventEmitter<IncidentEtaUpdate> = new EventEmitter<IncidentEtaUpdate>();
  public $serviceProviderStatusUpdate: EventEmitter<ServiceProviderStatusUpdate> = new EventEmitter<ServiceProviderStatusUpdate>();
  public $bdTechIncidentStatusUpdate: EventEmitter<BDTechIncidentStatusUpdate> = new EventEmitter<BDTechIncidentStatusUpdate>();

   // Loading states
  public isLoadingOpenIncidents = signal<boolean>(false);
  public isLoadingClosedIncidents = signal<boolean>(false);
  public isLoadingOperatorIncidents = signal<boolean>(false);

  constructor(private http: HttpClient, private authService: AuthService, private notificationService: NotificationService) { }

  public expandUrl(url: string): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(environment.apiUrl + this.apiUrl + 'expand-url', { params: { url } });
  }

  public getCallcentreNoToken(clientId: number): Observable<Client> {
    return this.http.get<Client>(environment.apiUrl + this.apiUrl + `notoken/callcentre/${clientId}`);
  }

  public getFleetIncidentsNoToken(clientId: number): Observable<Incident[]> {
    return this.http.get<Incident[]>(environment.apiUrl + this.apiUrl + `notoken/fleet/${clientId}/incidents`);
  }

  // OPEN INCIDENTS
  public loadOpenIncidents(): Observable<Incident[]> {
    this.isLoadingOpenIncidents.set(true);

    return this.getOpenIncidentList().pipe(
      map(incidents => {
        this.openIncidents.set(incidents);
        this.isLoadingOpenIncidents.set(false);
        //this.operatorIncidents = incidents;
        return incidents;
      }),
      catchError(error => {
        this.isLoadingOpenIncidents.set(false);
        throw error;
      })
    );
  }

  public loadOpenIncidentsForDateRange(startDate: Date, endDate: Date): Observable<Incident[]> {
    this.isLoadingOpenIncidents.set(true);
    
    return this.getOpenIncidentList(startDate, endDate).pipe(
      map(incidents => {
        this.openIncidents.set(incidents);
        this.isLoadingOpenIncidents.set(false);
        return incidents;
      }),
      catchError(error => {
        this.isLoadingOpenIncidents.set(false);
        throw error;
      })
    );
  }

  // CLOSED INCIDENTS
  public loadClosedIncidents(): Observable<Incident[]> {
    this.isLoadingClosedIncidents.set(true);

    return this.getClosedIncidentList().pipe(
      map(incidents => {
        this.closedIncidents.set(incidents);
        this.isLoadingClosedIncidents.set(false);
        return incidents;
      }),
      catchError(error => {
        this.isLoadingClosedIncidents.set(false);
        throw error;
      })
    );
  }

  public loadClosedIncidentsForDateRange(startDate: Date, endDate: Date): Observable<Incident[]> {
    this.isLoadingClosedIncidents.set(true);
    
    return this.getClosedIncidentList(startDate, endDate).pipe(
      map(incidents => {
        this.closedIncidents.set(incidents);
        this.isLoadingClosedIncidents.set(false);
        return incidents;
      }),
      catchError(error => {
        this.isLoadingClosedIncidents.set(false);
        throw error;
      })
    );
  }

  // COMBINED LOADING
  public loadTodayIncidents(): Observable<{open: Incident[], closed: Incident[]}> {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    this.isLoadingOpenIncidents.set(true);
    this.isLoadingClosedIncidents.set(true);

    return forkJoin({
      open: this.getOpenIncidentList(startOfDay, endOfDay),
      closed: this.getClosedIncidentList(startOfDay, endOfDay)
    }).pipe(
      map(result => {
        this.openIncidents.set(result.open);
        this.closedIncidents.set(result.closed);
        this.isLoadingOpenIncidents.set(false);
        this.isLoadingClosedIncidents.set(false);
        return result;
      }),
      catchError(error => {
        this.isLoadingOpenIncidents.set(false);
        this.isLoadingClosedIncidents.set(false);
        throw error;
      })
    );
  }

  public get openIncidents$() {
    return computed(() => this.openIncidents());
  }

  public get closedIncidents$() {
    return computed(() => this.closedIncidents());
  }

  public get isLoadingOpen$() {
    return computed(() => this.isLoadingOpenIncidents());
  }

  public get isLoadingClosed$() {
    return computed(() => this.isLoadingClosedIncidents());
  }

  // REFRESH METHODS
  public refreshOpenIncidents(): void {
    this.loadOpenIncidents().subscribe();
  }

  public refreshClosedIncidents(): void {
    this.loadClosedIncidents().subscribe();
  }

  public refreshAllIncidents(): void {
    this.loadTodayIncidents().subscribe();
  }

  public loadOperatorIncidents(): Observable<Incident[]> {
    this.isLoadingOperatorIncidents.set(true);
    
    return this.getOpenIncidentList().pipe(
      map(incidents => {
        this.operatorIncidents = incidents;
        this.isLoadingOperatorIncidents.set(false);
        return incidents;
      })
    );
  }

  public loadDashboardStats(): Observable<DashboardStats> {
    return this.http.get<DashboardStats>(environment.apiUrl + this.apiUrl + 'dashboard-stats');
  }

  public getTodayStats() {
    const todayOpen = this.openIncidents();
    const todayClosed = this.closedIncidents();    
    return {
      breakdownsToday: todayOpen.filter(i => new Date(i.dtLogged).getDate() === new Date().getDate()).length,
      cancelledIncidentsToday: [...todayOpen, ...todayClosed].filter(incident => incident.statusId > 90).length
    };
  }

  public getOperatorStats(clientId: number | undefined) {
    if (!clientId) return { newIncidentCount: 0, openIncidentCount: 0 };
    
    const incidents = this.openIncidents();
    
    return {
      newIncidentCount: incidents.filter(incident => 
        incident.incidentOperators.filter(e => e.clientId === clientId).length === 0
      ).length,
      openIncidentCount: incidents.filter(incident => incident.statusId <= 10).length
    };
  }

  public getFirstNewIncidentId(clientId: number | undefined): number | undefined {
    if (!clientId) return undefined;
    
    return this.openIncidents().find(incident => 
      incident.incidentOperators.filter(e => e.clientId === clientId).length === 0
    )?.incidentId;
  }

  public updateOrAddIncidentToList(incident: Incident) {
    console.log("Updating or adding incident to list:", incident);

    this.openIncidents.update((currentIncidents) => {
      const updatedList = [...currentIncidents];
      const index = updatedList.findIndex(i => i.incidentId === incident.incidentId);

      if (index !== -1) {
        if (incident.statusId > 10) {
          // If the incident has been cancelled, remove it from the list
          updatedList.splice(index, 1);
        } else {
          //before we update, do some checks
          //First check is a secondary callcentre has accepted
          const existingIncident = updatedList[index];

          // Check if a secondary callcentre has accepted or declined
          const currentUserClientId = this.authService.user?.clientId;
          const pendingCallcentres = (existingIncident.incidentClients ?? []).filter(
            link => link.connectionTypeId === 2 && link.isActive == null && link.clientId !== currentUserClientId
          );
          for (const candidate of pendingCallcentres) {
            const incomingLink = (incident.incidentClients ?? []).find(l => l.clientId === candidate.clientId);
            if (incomingLink && incomingLink.isActive === true) {
              this.notificationService.showSuccess(`${candidate.clientName} accepted the incident`);
            } else if (!incomingLink) {
              this.notificationService.showWarning(`${candidate.clientName} declined the incident`);
            }
          }

          // Update existing incident, preserving incidentActions if the incoming payload omitted it
          if (incident.incidentActions === null && existingIncident.incidentActions !== null) {
            incident.incidentActions = existingIncident.incidentActions;
          }
          updatedList[index] = incident;
        }
      } else {
        if (incident.statusId <= 10) {
          updatedList.unshift(incident);
        }
      }

      return updatedList;
    })

    console.log("Emitting changes for incident");
    this.$incidentUpdate.emit(incident);

    // const index = this.openIncidents().findIndex(i => i.incidentId === incident.incidentId);
    // if (index !== -1) {
    //   //if the incident has been cancelled, remove it from the list
    //   if (incident.statusId > 10) {
    //     this.openIncidents().splice(index, 1);
    //   } else {
    //     // Update existing incident
    //     this.openIncidents()[index] = incident;
    //     console.log("Updated existing incident at index", index);
    //   }
    // } else {
    //   // Add new incident
    //   this.openIncidents().push(incident);
    //   console.log("Added new incident to the list");
    // }
    // console.log("Emitting changes for incident");
    // this.$incidentUpdate.emit(incident);
  }

  public emitIncidentUpdate(incident: Incident) {
    this.$incidentUpdate.emit(incident);
  }

  public emitIncidentEtaUpdate(etaUpdate: IncidentEtaUpdate) {
    this.$etaUpdate.emit(etaUpdate);
  }

  public createIncident(incident: Incident) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + 'create', incident);
  }

  public getIncident(incidentId: number) {
    return this.http.get<Incident>(environment.apiUrl + this.apiUrl + incidentId);
  }

  public getIncidentNoToken(incidentId: number, clientId: number, cacheBuster: string = '') {
    const url = environment.apiUrl + this.apiUrl + incidentId + `/client/${clientId}`;
  
    let params = new HttpParams();
    if (cacheBuster) {
      // Appends ?cb=123456789 to the end of the request
      params = params.set('cb', cacheBuster); 
    }

    return this.http.get<Incident>(url, { params });
  }

  public deleteIncident(incidentId: number, reasonId: number) {
    return this.http.delete<Incident>(environment.apiUrl + this.apiUrl + incidentId + '/' + reasonId);
  }

  public getOpenIncidentList( startDate?: Date, endDate?: Date) {
  let params = new HttpParams();
  
  if (startDate) {
    params = params.set('startDate', startDate.toISOString());
  }
  if (endDate) {
    params = params.set('endDate', endDate.toISOString());
  }
  
  return this.http.get<Incident[]>(
    environment.apiUrl + this.apiUrl + 'open/list', 
    { params }
  );
}

  public getClosedIncidentList( startDate?: Date, endDate?: Date) {
  let params = new HttpParams();
  
  if (startDate) {
    params = params.set('startDate', startDate.toISOString());
  }
  if (endDate) {
    params = params.set('endDate', endDate.toISOString());
  }
  
  return this.http.get<Incident[]>(
    environment.apiUrl + this.apiUrl + 'closed/list', 
    { params }
  );
}

  public assignIncidentToOperator(incidentId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + 'addoperator/' + incidentId, null);
  }

  public assignCallcentre(incidentId: number, clientId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `addcallcentre/${incidentId}/${clientId}`, null);
  }

  public updateIncident(incident: Incident) {
    return this.http.put<Incident>(environment.apiUrl + this.apiUrl + incident.incidentId, incident);
  }

  public updateIncidentNoToken(incident: Incident, clientId: number, loadServiceProviders: number = 0) {
    return this.http.put<Incident>(environment.apiUrl + `${this.apiUrl}addorupdate/${incident.incidentId}/${clientId}/${loadServiceProviders}`, incident);
  }

  public addOrUpdateFleetIncidentNoToken(incident: Incident, addServiceProviders: number) {
    return this.http.put<Incident>(environment.apiUrl + `${this.apiUrl}addorupdate/${incident.incidentId}/${addServiceProviders}`, incident);
  }

  public dataCaptureIncident(incident: Incident) {
    return this.http.patch<Incident>(environment.apiUrl + this.apiUrl + incident.incidentId, incident);
  }

  public sendWhatsappTemplate(templateId: number, incident: Incident) {
    return this.http.post(environment.apiUrl + this.apiUrl + 'whatsapp/' + templateId, incident);
  }

  public requestBreakdownTech(incidentId: number, breakdownTechId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/requestBreakdownDriver/${breakdownTechId}`, null);
  }

  public informBreakdownTech(incidentId: number, breakdownTechId: number, incident: Incident) {
    //currently the same flow as requestBreakdownTech - backend may change later
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/requestBreakdownDriver/${breakdownTechId}`, incident);
  }

  public callcentreAcceptIncident(incidentId: number, clientId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `callcentre/accept/${incidentId}/${clientId}`, null);
  }

  public callcentreRejectIncident(incidentId: number, clientId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `callcentre/reject/${incidentId}/${clientId}`, null);
  }

  public linkServiceProviders(incidentId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/linkServiceProviders`, null);
  }

  public linkServiceProvider(incidentId: number, clientId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/linkServiceProvider/${clientId}`, null);
  }

  public notifyServiceProviderOfIncident(incidentId: number, serviceProviderId: number, incident: Incident) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/notifyServiceProvider/${serviceProviderId}`, incident);
  }

  public notifyAllUsersViaWhatsapp(incident: Incident) {
    return this.http.post(environment.apiUrl + this.apiUrl + `${incident.incidentId}/notifydetailupdate`, incident);
  }

  public assignServiceProviderToIncident(incidentId: number, serviceProviderId: number, incident: Incident) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/assignServiceProvider/${serviceProviderId}`, incident);
  }

  public assignBreakdownTechnicianToIncident(incidentId: number, breakdownTechId: number, incident: Incident) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/assignBreakdownTechnician/${breakdownTechId}`, incident);
  }

  public searchForIncidents(searchRequest: IncidentSearchRequest) {
    return this.http.post<Incident[]>(environment.apiUrl + this.apiUrl + 'search', searchRequest);
  }

  public assignWhatsappMessageToIncident(incidentId: number, messageId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/assignWhatsappMessage/${messageId}`, null);
  }

  public assignWorkReportToIncident(incidentId: number, documentId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/assignWorkReport/${documentId}`, null);
  }

  public completeWithoutWorkReport(incidentId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/completeWithoutWorkReport`, null);
  }
  

  public closeIncident(incidentId: number, closeRequest: IncidentCloseRequest) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/close`, closeRequest);
  }

  public findNearbyServiceProviders(latitude: number, longitude: number) {
    return this.http.get<any>(environment.apiUrl + this.apiUrl + `serviceProviders/nearby`, {
      params: new HttpParams()
        .set('latitude', latitude.toString())
        .set('longitude', longitude.toString())
    });
  }


  public uploadFile(file: UploadingFile, incidentId: number) {
    return this.http.post<Document>(environment.apiUrl + this.apiUrl + `${incidentId}/uploadFile`, file);
  }

  public removeFileFromIncident(documentId: number, incidentId: number) {
    return this.http.delete<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/removeFile/${documentId}`);
  }

  public reclassifyDocument(documentId: number, incidentId: number, newClassificationId: number) {
    return this.http.put<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/reclassifyDocument/${documentId}/${newClassificationId}`, null);
  }

  public addDelayToIncident(incident: Incident, delayMinutes: number, delayReason: string) {
    let request = new IncidentDelayRequest();
    request.incident = incident;
    request.delayMinutes = delayMinutes;
    request.delayReason = delayReason;
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `addDelay`, request);
  }

  public serviceProviderAcceptIncident(incident: Incident, delayMinutes: number) {
    let request = new IncidentDelayRequest();
    request.incident = incident;
    request.delayMinutes = delayMinutes;
    request.delayReason = "Service Provider Accepted";
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incident.incidentId}/serviceProviderAccept`, request);
  }

  public serviceProviderRejectIncident(incidentId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/serviceProviderReject`, null);
  }

  public breakdownTechnicianCompleted(incidentId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/bdtechcomplete`, null);
  }

  public sendPhoto(incidentId: number, docId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/send-photo/${docId}`, null);
  }
  
  public sendPOW(incidentId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/send-proofofwork`, null);
  }

  public unassignSPFromIncident(incidentId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/unassignServiceProvider`, null);
  }

  public addIncidentLog(incidentId: number, logType: number, comment: string) {
    let request = new IncidentLogRequest();
    request.incidentId = incidentId;
    request.logTypeId = logType;
    request.comment = comment;
    return this.http.post(environment.apiUrl + this.apiUrl + `log/${incidentId}`, request);
  }

  public writeIncidentLogBackground(incidentId: number, logType: number, comment: string) {
    this.addIncidentLog(incidentId, logType, comment).subscribe({
      next: () => console.log("Incident log added successfully"),
      error: (err) => console.error("Failed to add incident log:", err)
    });
  }

  public uploadFileNoToken(file: UploadingFile, incidentId: number) {
    return this.http.post<Document>(environment.apiUrl + this.apiUrl + `${incidentId}/notoken/uploadFile`, file);
  }

  public removeFileFromIncidentNoToken(documentId: number, incidentId: number) {
    return this.http.delete<void>(environment.apiUrl + this.apiUrl + `${incidentId}/notoken/removeFile/${documentId}`);
  }

  public bdtechAcceptIncidentNoToken(incident: Incident) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incident.incidentId}/notoken/bdtechaccept`, incident);
  }

  public bdtechConfirmEtaNoToken(confirm: EtaConfirm) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${confirm.incidentId}/notoken/bdtechconfirmeta`, confirm);
  }

  public breakdownTechnicianCompletedNoToken(incidentId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/notoken/bdtechcomplete`, null);
  }

  public breakdownTechnicianArrivedNoToken(incidentId: number) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/notoken/bdtecharrived`, null);
  }

  public addDelayNoToken(incidentId: number, delayMinutes: number, delayReason: string) {
    const request = new DelayAdded();
    request.incidentId = incidentId;
    request.delayMinutes = delayMinutes;
    request.delayReason = delayReason;
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `${incidentId}/notoken/addDelay`, request);
  }

  public decodeIncidentMessage(message: string) {
    return this.http.post<Incident>(environment.apiUrl + this.apiUrl + `decode_message`, { 'message': message });
  }
}
